import { useQuery } from '@tanstack/react-query';
import {
  getLatestMeasurements,
  type HistorySource,
  type LatestParameter,
  type LatestValue,
  type ParameterTarget,
  type ParameterTrend,
} from '../services/measurementService';

/**
 * Latest water value per parameter (release 1.5.0, bug 3 / feature 2).
 *
 * One backend endpoint (GET /aquariums/:id/measurements/latest) is the single
 * source of truth for the dashboard tiles and the latest-values table of
 * "Messwert hinzufügen": per parameter the newest value by measurement time,
 * whatever its source (manual, photo, ICP), plus the previous value and trend.
 *
 * The query lives under ['measurements', aquariumId, 'latest'], so every save,
 * edit or delete that invalidates ['measurements'] (useSubmitMeasurement, the
 * photo review, the history dialogs) refreshes the dashboard too.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Where the measurement value originated (as the history shows it). */
export type MeasurementSource = HistorySource;

/** Status label derived from comparing a value against optimal ranges */
export type MeasurementStatus =
  | 'excellent'
  | 'stable'
  | 'warning'
  | 'critical'
  | 'unknown';

/** A single resolved measurement for one parameter */
export interface ParameterMeasurement {
  /** The requested parameter key (e.g. 'kh', 'no3', 'po4') */
  readonly parameterKey: string;
  /** Name the backend sends (ICP elements have no translated name). */
  readonly parameterName?: string;
  /** Measured numeric value */
  readonly value: number;
  /** ISO 8601 timestamp of the measurement */
  readonly timestamp: string;
  /** Which data source provided this value */
  readonly source: MeasurementSource;
  /** Derived status label (position in the target range — not a trend) */
  readonly status: MeasurementStatus;
  /** Human-readable unit string (e.g. 'dKH', 'ppm') */
  readonly unit: string | null;
  /** The measurement before this one (any source); null/absent when there is only one. */
  readonly previous?: LatestValue | null;
  /** Change from `previous` to this value; null/absent without a comparable previous value. */
  readonly trend?: ParameterTrend | null;
}

/** Return shape of useLatestMeasurements */
export interface LatestMeasurementsResult {
  /** True while the fetch is in flight */
  readonly isLoading: boolean;
  /** The fetch error, or null */
  readonly error: Error | null;
  /** Map of parameterKey → resolved measurement (null if no data for that key) */
  readonly data: Record<string, ParameterMeasurement | null>;
  /** Re-runs the fetch (e.g. the Retry button of a load error). */
  readonly refetch?: () => void;
}

// ---------------------------------------------------------------------------
// Status derivation
// ---------------------------------------------------------------------------

/**
 * Derives a MeasurementStatus from a value and the target the backend resolved
 * for it (personal set point or hobby default — never a local range table, so
 * the dashboard, health score, alerts and charts always agree):
 *   - value within [min, max]                       → 'excellent'
 *   - within one margin outside                     → 'stable'
 *   - within two margins outside                    → 'warning'
 *   - beyond                                        → 'critical'
 *   - no target for the parameter                   → 'unknown'
 */
export function deriveStatus(target: ParameterTarget | null | undefined, value: number): MeasurementStatus {
  if (!target || !Number.isFinite(value)) return 'unknown';
  if (value >= target.min && value <= target.max) return 'excellent';
  const margin = target.margin ?? 0;
  const distance = value < target.min ? target.min - value : value - target.max;
  if (distance <= margin) return 'stable';
  if (distance <= margin * 2) return 'warning';
  return 'critical';
}

// ---------------------------------------------------------------------------
// Parameter keys → stored parameter ids
// ---------------------------------------------------------------------------

/**
 * Short keys (dashboard) and aliases → the parameter id stored in
 * aquarium_measurements (frontend/src/types/measurement.ts PARAMETER_OPTIONS;
 * ICP values use the same ids, src/services/icpMeasurements.ts). Unknown keys
 * are looked up as they are (e.g. ICP element symbols such as "Sr").
 */
const PARAMETER_ID_ALIASES: Readonly<Record<string, string>> = {
  kh: 'alkalinity',
  alk: 'alkalinity',
  alkalinity: 'alkalinity',
  ca: 'calcium',
  calcium: 'calcium',
  mg: 'magnesium',
  magnesium: 'magnesium',
  no3: 'nitrate',
  nitrate: 'nitrate',
  po4: 'phosphate',
  phosphate: 'phosphate',
  i: 'iodine',
  iodine: 'iodine',
  k: 'potassium',
  potassium: 'potassium',
  temp: 'temperature',
  temperature: 'temperature',
  salinity: 'salinity',
  ph: 'ph',
};

/** The stored parameter id of a requested key. */
export function parameterIdFor(key: string): string {
  const lower = key.toLowerCase();
  return Object.hasOwn(PARAMETER_ID_ALIASES, lower)
    ? (PARAMETER_ID_ALIASES[lower as keyof typeof PARAMETER_ID_ALIASES] ?? key)
    : key;
}

/**
 * Picks the requested parameters out of the latest-values list. Pure, so the
 * bug-3 regression can be tested without a network.
 */
export function selectLatestMeasurements(
  parameters: readonly LatestParameter[],
  keys: readonly string[],
): Record<string, ParameterMeasurement | null> {
  const byId = new Map(parameters.map((p) => [p.parameterId, p]));
  const result: Record<string, ParameterMeasurement | null> = {};
  for (const key of keys) {
    const entry = byId.get(parameterIdFor(key)) ?? byId.get(key) ?? null;
    if (entry === null) {
      result[key] = null;
      continue;
    }
    const { latest } = entry;
    result[key] = {
      parameterKey: key,
      parameterName: entry.parameterName,
      value: latest.value,
      timestamp: latest.measuredAt,
      source: latest.source,
      status: deriveStatus(entry.target, latest.value),
      unit: latest.unit.trim() === '' ? null : latest.unit,
      previous: entry.previous,
      trend: entry.trend,
    };
  }
  return result;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Query key of the latest values; under ['measurements', aquariumId] so measurement saves invalidate it. */
export function latestMeasurementsKey(aquariumId: string | null | undefined): readonly unknown[] {
  return ['measurements', aquariumId, 'latest'];
}

/** Every parameter of the aquarium with its newest value (latest-values table). */
export function useLatestParameters(aquariumId: string | null | undefined) {
  return useQuery<LatestParameter[], Error>({
    queryKey: latestMeasurementsKey(aquariumId),
    queryFn: () => getLatestMeasurements(aquariumId as string),
    enabled: Boolean(aquariumId),
  });
}

/**
 * The newest value of each requested parameter for a given aquarium,
 * whatever its source (dashboard tiles).
 *
 * @example
 * const { isLoading, error, data } = useLatestMeasurements(aquariumId, ['kh', 'no3', 'po4']);
 * const khMeasurement = data['kh']; // ParameterMeasurement | null
 */
export function useLatestMeasurements(
  aquariumId: string | null | undefined,
  parameters: readonly string[],
): LatestMeasurementsResult {
  const enabled = Boolean(aquariumId) && parameters.length > 0;

  const query = useQuery<LatestParameter[], Error>({
    queryKey: latestMeasurementsKey(aquariumId),
    queryFn: () => getLatestMeasurements(aquariumId as string),
    enabled,
  });

  // isPending is true for disabled queries that have never fetched (React Query v5).
  const isLoading = enabled && query.isPending;
  const data = selectLatestMeasurements(enabled ? (query.data ?? []) : [], parameters);
  const refetch = (): void => {
    void query.refetch();
  };

  return { isLoading, error: query.error ?? null, data, refetch };
}
